import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { formatHumanName } from "@medplum/core";
import { Bundle, Patient, RelatedPerson } from "@medplum/fhirtypes";
import { useMedplum, useSubscription } from "@medplum/react";

import { Box, Flex } from "@mantine/core";

import { BackgroundContext } from "@/contexts/background";
import { PartnerContext } from "@/contexts/partner";
import { PatientContext } from "@/contexts/patient";
import { RelatedPersonRelationshipTypes } from "@/profile/constants/related-person-relationship";
import { getPatientRelatedPersons } from "@/profile/services/related-person";
import { getResourcesFromBundle } from "@/utils/medplum";

import ClientProfile from "../ClientProfile/ClientProfile";
import ClientTabs from "../ClientTabs/ClientTabs";

export default function ClientDetail() {
  const { clientId } = useParams() as { clientId: string };
  const InitialPatientContext = useContext(PatientContext);
  const [partners, setPartners] = useState<RelatedPerson[]>([]);
  const [patient, setPatient] = useState<Patient | undefined>(
    InitialPatientContext.patient ?? undefined,
  );
  const medplum = useMedplum();
  const { setBackground } = useContext(BackgroundContext);

  useEffect(() => {
    if (InitialPatientContext.patient?.id === clientId) {
      setPatient(InitialPatientContext.patient);
    }
  }, [clientId, InitialPatientContext.patient]);

  // Set transparent background for client detail page
  useEffect(() => {
    setBackground("transparent");
    return () => setBackground("cream.0");
  }, [setBackground]);

  const fetchAndSetPartners = useCallback(async () => {
    if (!clientId) return;

    try {
      // Invalidate search cache to ensure we get the latest data
      medplum.invalidateSearches("RelatedPerson");

      const allRelatedPersons = (await getPatientRelatedPersons(
        medplum,
        clientId,
      )) as RelatedPerson[];

      if (!allRelatedPersons.length) {
        setPartners([]);
        return;
      }

      // Filter partners by relationship type (Spouse, Domestic Partner, or Significant Other)
      const partnersByRelationship = allRelatedPersons.filter(isPartner);

      // Deduplicate partners by ID and name to avoid showing duplicates with the same name and relationship
      const uniquePartners = partnersByRelationship.filter((partner, index, self) => {
        const partnerName = formatHumanName(partner?.name?.[0]);
        const partnerRelationship =
          partner.relationship?.[0]?.text || partner.relationship?.[0]?.coding?.[0]?.display;

        return (
          index ===
          self.findIndex((internalPartner) => {
            const internalPartnerName = formatHumanName(internalPartner?.name?.[0]);
            const internalPartnerRelationship =
              internalPartner.relationship?.[0]?.text ||
              internalPartner.relationship?.[0]?.coding?.[0]?.display;

            return (
              partnerName === internalPartnerName &&
              partnerRelationship === internalPartnerRelationship
            );
          })
        );
      });

      setPartners([...uniquePartners]);
    } catch (error) {
      console.error("Error fetching partners:", error);
      setPartners([]);
    }
  }, [medplum, clientId]);

  useSubscription(`Patient?_id=${clientId}`, (bundle: Bundle) => {
    const updatedPatient = getResourcesFromBundle("Patient", bundle)[0] as Patient;

    setPatient(updatedPatient);
  });

  // Helper function to check if a RelatedPerson is a partner
  const isPartner = (relatedPerson: RelatedPerson): boolean => {
    return (
      relatedPerson.relationship?.some((relationship) => {
        const display = relationship.text || relationship.coding?.[0]?.display;
        if (!display) return false;
        return Object.values(RelatedPersonRelationshipTypes).includes(
          display as RelatedPersonRelationshipTypes,
        );
      }) ?? false
    );
  };

  useSubscription(
    `RelatedPerson?patient=Patient/${clientId}`,
    async () => {
      if (!clientId) return;

      try {
        // Always refetch when subscription fires to ensure we catch all changes including deletions
        // History bundles from subscriptions may not reliably indicate deletions, so refetching
        // is the safest approach. Cache invalidation ensures we get fresh data.
        await fetchAndSetPartners();
      } catch (error) {
        console.error("Error fetching partners from subscription:", error);
      }
    },
    {
      onError: (error: Error) => {
        console.error("Error in RelatedPerson subscription:", error);
      },
    },
  );

  useEffect(() => {
    const fetchPatient = async () => {
      // TODO: properly handle 404 errors
      const patient = await medplum.searchOne("Patient", {
        _id: clientId,
      });
      setPatient(patient);
    };

    fetchPatient();
  }, [medplum, clientId]);

  useEffect(() => {
    fetchAndSetPartners();
  }, [fetchAndSetPartners]);

  // Memoize context value to prevent unnecessary re-renders
  const partnerContextValue = useMemo(
    () => ({ partners, setPartners, refreshPartners: fetchAndSetPartners }),
    [partners, setPartners, fetchAndSetPartners],
  );

  const patientContextValue = useMemo(() => ({ patient, setPatient }), [patient, setPatient]);

  return (
    <PatientContext.Provider value={patientContextValue}>
      <PartnerContext.Provider value={partnerContextValue}>
        {/* Below lg the profile stacks above the chart, so tablets give the chart the full width. */}
        <Flex
          gap={0}
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "flex-start" }}
        >
          <ClientProfile />
          <Box flex={1}>
            <ClientTabs />
          </Box>
        </Flex>
      </PartnerContext.Provider>
    </PatientContext.Provider>
  );
}
